import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { DatasetResult } from '@/types/dashboard';

interface PhaseComparisonChartProps {
  datasets: DatasetResult[];
}

function shortenName(name: string): string {
  return name
    .replace(/Venues? with /i, '')
    .replace(/ in NL$/i, '')
    .replace(/ Options/i, '')
    .replace(/Canal-side /i, '')
    .replace(/Waterfront \/ /, 'Waterfront ')
    .replace(/ Venues/i, '');
}

const COLORS = {
  phase1Recall: '#6ee7b7', // green-300
  phase2Recall: '#22d3ee', // cyan
  rerankerDamage: '#f87171', // red-400
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    payload: { fullName: string; damage: number; damageNames: string };
  }>;
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="rounded-lg border border-primary/30 bg-card/95 backdrop-blur-md p-3 shadow-xl max-w-xs">
      <div className="font-semibold text-sm text-foreground mb-2">{data.fullName}</div>
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.name} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-muted-foreground">{p.name}</span>
            </div>
            <span className="font-bold tabular-nums" style={{ color: p.color }}>
              {p.value.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
      {data.damage > 0 && data.damageNames && (
        <div className="mt-2 pt-2 border-t border-primary/10 text-[10px] text-red-400/80">
          Killed: {data.damageNames}
        </div>
      )}
    </div>
  );
}

export function PhaseComparisonChart({ datasets }: PhaseComparisonChartProps) {
  const chartData = useMemo(() => {
    return datasets
      .filter((d) => d.status === 'completed' && d.phase1 && d.phase2)
      .map((d) => {
        const damageNames = (d.reranker_damage ?? []).map((rd) => rd.name).join(', ');
        return {
          name: shortenName(d.dataset_name),
          fullName: d.dataset_name,
          phase1Recall: d.phase1!.recall,
          phase2Recall: d.phase2!.recall,
          damage: d.phase1!.recall - d.phase2!.recall,
          damageNames,
        };
      })
      .sort((a, b) => a.damage - b.damage);
  }, [datasets]);

  if (chartData.length === 0) return null;

  return (
    <div className="rounded-xl border border-primary/20 bg-card/50 p-5 backdrop-blur-xs">
      <h3 className="text-base font-bold bg-linear-to-r from-primary to-orange-400 bg-clip-text text-transparent mb-1">
        Phase 1 vs Phase 2 Recall
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Shows how much recall the reranker preserves (or loses) per dataset
      </p>
      <ResponsiveContainer width="100%" height={Math.max(260, chartData.length * 38 + 60)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--primary) / 0.08)" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            tickFormatter={(v: number) => `${v}%`}
            axisLine={{ stroke: 'hsl(var(--primary) / 0.15)' }}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--primary) / 0.05)' }} />
          <Legend
            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            formatter={(value: string) => <span style={{ color: 'hsl(var(--muted-foreground))' }}>{value}</span>}
          />
          <Bar dataKey="phase1Recall" name="Phase 1 Recall" fill={COLORS.phase1Recall} radius={[0, 3, 3, 0]} barSize={12}>
            {chartData.map((_, i) => (
              <Cell key={i} fillOpacity={0.6} />
            ))}
          </Bar>
          <Bar dataKey="phase2Recall" name="Phase 2 Recall" fill={COLORS.phase2Recall} radius={[0, 3, 3, 0]} barSize={12}>
            {chartData.map((_, i) => (
              <Cell key={i} fillOpacity={0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
